import { useEffect, useState } from "react";
import { Spin } from "antd";
import {
  useLazyAppConfigQuery,
  useLazyAppLocalesQuery,
} from "src/store/apis/BaseApi";
import { useDispatch } from "react-redux";
import { reset, setAppConfig } from "src/store/slices/appConfig";
import { resetLocales, setAppLocales } from "src/store/slices/appLocales";
import { hexToRgb, normalizeBy } from "src/utility/utility";

export function withAppConfig<P extends object>(Component: React.ComponentType<P>) {
  return function (props: P) {
    const [ready, setReady] = useState(false);
    const dispatch = useDispatch();

    const [getAppConfig, { isLoading: isLoadingConfig }] =
      useLazyAppConfigQuery();
    const [getAppLocales, { isLoading: isLoadingLocales }] =
      useLazyAppLocalesQuery();

    const setupAppConfig = () => {
      getAppConfig(undefined)
        .unwrap()
        .then((res) => {
          const colors = {
            primaryColor: res.primary_color,
            secondaryColor: res.secondary_color,
            textColor: res.text_color,
            dangerColor: res.danger_color,
            successColor: res.success_color,
            whiteColor: res.white_color,
            blackColor: res.black_color,
          };

          const baseUrl = res.base_api_url;
          const variant = res.variant;
          const darkLogo = res.darkLogo;
          const lightLogo = res.lightLogo;
          const favicon = res.favicon;

          dispatch(
            setAppConfig({
              colors,
              baseUrl,
              variant,
              darkLogo,
              lightLogo,
              favicon,
            })
          );
        })
        .catch(() => {
          dispatch(reset());
        })
        .finally(() => {
          setReady(true);
        });
    };
    const setupAppLocales = () => {
      getAppLocales(undefined)
        .unwrap()
        .then((res) => {
          const locales: Record<string, Record<string, string>> = {
            ar: {},
            en: {},
          };
          res?.forEach((item): void => {
            locales[item.language] = normalizeBy<any, string>(
              item.keys,
              "key",
              (key) => key.value
            );
          });
          dispatch(setAppLocales({ locales }));
        })
        .catch(() => {
          dispatch(resetLocales());
        })
        .finally(() => {
          setReady(true);
        });
    };

    useEffect(() => {
      setupAppConfig();
      setupAppLocales();
    }, []);

    const isLoading = isLoadingConfig || isLoadingLocales;
    if (isLoading || !ready) {
      return (
        <Spin size="large" spinning={isLoading}>
          <div style={{ height: "100vh", width: "100vw" }} />
        </Spin>
      );
    }

    return <Component {...props} />;
  };
}
